import * as React from 'react'
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from '@mui/material'

import { OptionListAutocomplete } from '../core/OptionListAutocomplete'
import { SectionCard } from './SectionCard'
import { BillingOrderSelect } from './BillingOrderSelect'
import type { WorkScopeRegistryFieldValues } from '../../types/Work/WorkEntry'
import { getFormDefinition, listFormDefinitions } from '../../services/formDefinitionsService'
import type { FormDefinitionDetail, FormField } from '../../types/Form/FormDefinition'
import type { ProjectOption } from '../../types/Form/ProjectOption'
import { getBillingLogEntries } from '../../services/billingLogService'
import {
	formNeedsWorkOrder,
	getDefaultWorkOrder,
	type BillingLogEntryResponse,
	type BillingOrderValues,
} from '../../types/Contracts/BillingLog'
import { getNextWsrTagNumber } from '../../services/WSRService'
import { showErrorToast } from '../../services/CommonServices/alertServices'
import {
	buildFormFieldDimensionPairRows,
	buildOrderedDynamicFormSections,
	chunkArray,
	fieldIsLocked,
	sortFormFieldOptions,
} from '../../utils/dynamicFormsUtils'

const WORK_SCOPE_REGISTRY_CODE = 'work_scope_registry'

/** Match reference layout: left column first, then right. */
const SECTION_DISPLAY_ORDER = [
	'Project Information',
	'WSR Details',
	'Scaffold Spec/Type/Configuration',
	'Dimensions (Metric)',
	'Location Information',
] as const

const LEFT_COLUMN_SECTIONS = new Set<string>(['Project Information', 'WSR Details'])

type Props = {
	registryFieldValues: WorkScopeRegistryFieldValues
	onRegistryPatch: (patch: Record<string, string>) => void
	onClear: () => void
	onFormLoaded?: (formDetail: FormDefinitionDetail, billingLogs: BillingLogEntryResponse[]) => void
	onSubmitNewWorkscope?: () => void
	onUpdateWorkscope?: () => void
	submitting?: boolean
	submitDisabled?: boolean
	updateDisabled?: boolean
	loadedTagId?: number | null
	savedBillingOrder?: BillingOrderValues
}

export function WorkEntryRegistryForm({
	registryFieldValues,
	onRegistryPatch,
	onClear,
	onFormLoaded,
	onSubmitNewWorkscope,
	onUpdateWorkscope,
	submitting = false,
	submitDisabled = false,
	updateDisabled = false,
	loadedTagId = null,
	savedBillingOrder,
}: Props) {
	const [formDetail, setFormDetail] = React.useState<FormDefinitionDetail | null>(null)
	const [generating, setGenerating] = React.useState(false)
	const [billingLogs, setBillingLogs] = React.useState<BillingLogEntryResponse[]>([])
	const [loading, setLoading] = React.useState(true)
	const [loadError, setLoadError] = React.useState<string | null>(null)

	const projectOptions = React.useMemo(
		() => formDetail?.fields.find((f) => f.fieldKey === 'project')?.projectOptions ?? [],
		[formDetail],
	)
	const projectOption = registryFieldValues['project'] ?? ''
	const selectedProject = projectOptions.find((o) => o.value === projectOption)
	const workTypes = selectedProject?.workTypes.filter((w) => w.id > 0) ?? []
	const workTypeId = Number(registryFieldValues['projectOptionWorkTypeId'] || 0)
	const selectedWorkType = workTypes.find((w) => w.id === workTypeId)

	React.useEffect(() => {
		let cancelled = false
		;(async () => {
			setLoading(true)
			setLoadError(null)
			try {
				// TODO: get the form definition using form code
				const list = await listFormDefinitions()
				const summary = list.find((f) => f.code === WORK_SCOPE_REGISTRY_CODE)
				if (!summary) {
					if (!cancelled) {
						setFormDetail(null)
						setLoadError(
							'No Work Scope Registry form is configured for this project (code work_scope_registry).',
						)
					}
					return
				}
				const [detail, logs] = await Promise.all([
					getFormDefinition(summary.id),
					getBillingLogEntries(),
				])
				if (!cancelled) {
					setBillingLogs(logs)
					setFormDetail(detail)
					onFormLoaded?.(detail, logs)
				}
			} catch {
				if (!cancelled) {
					setFormDetail(null)
					setLoadError('Could not load form configuration.')
				}
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [])

	const orderedSections = React.useMemo(
		() => (formDetail ? buildOrderedDynamicFormSections(formDetail, SECTION_DISPLAY_ORDER) : []),
		[formDetail],
	)

	const leftSections = React.useMemo(
		() => orderedSections.filter((s) => LEFT_COLUMN_SECTIONS.has(s.sectionName)),
		[orderedSections],
	)

	const rightSections = React.useMemo(
		() => orderedSections.filter((s) => !LEFT_COLUMN_SECTIONS.has(s.sectionName)),
		[orderedSections],
	)

	React.useEffect(() => {
		if (!formDetail?.fields?.length) return
		const patch: Record<string, string> = {}
		for (const f of formDetail.fields) {
			if (f.isHidden) continue
			const cur = registryFieldValues[f.fieldKey]
			if (f.fieldKey === 'project' && f.fieldType === 'dropdown') {
				if (cur !== undefined && cur !== '') continue
				const opts = projectOptions
				const next = opts.length > 0 ? opts[0].value : ''
				if (cur === next) continue
				patch[f.fieldKey] = next
				continue
			}
			if (cur === undefined) {
				patch[f.fieldKey] = ''
			}
		}
		if (Object.keys(patch).length > 0) {
			onRegistryPatch(patch)
		}
	}, [formDetail, projectOptions, registryFieldValues, onRegistryPatch])

	const setField = (fieldKey: string, value: string) => {
		if (fieldKey === 'project') {
			onRegistryPatch({ project: value, projectOptionWorkTypeId: '', wsrIdTag: '' })
		} else if (fieldKey === 'purchaseOrder') {
			onRegistryPatch({ purchaseOrder: value, workOrder: getDefaultWorkOrder(billingLogs, value) })
		} else {
			onRegistryPatch({ [fieldKey]: value })
		}
	}

	const renderField = (f: FormField) => {
		const value = registryFieldValues[f.fieldKey] ?? ''
		const locked = fieldIsLocked(f)
		const label = f.label

		const common = {
			label,
			size: 'small' as const,
			fullWidth: true,
			required: f.isRequired,
			disabled:
				submitting || generating || (f.fieldKey === 'project' ? loadedTagId !== null : locked),
			helperText: f.helpText || undefined,
			placeholder: f.placeholder ?? undefined,
		}

		if (f.fieldKey === 'purchaseOrder' || f.fieldKey === 'workOrder') {
			return (
				<BillingOrderSelect
					key={f.id}
					fieldKey={f.fieldKey}
					label={label}
					value={value}
					purchaseOrder={registryFieldValues['purchaseOrder'] ?? ''}
					logs={billingLogs}
					onChange={(next) => setField(f.fieldKey, next)}
					required={
						f.fieldKey === 'purchaseOrder' ||
						formNeedsWorkOrder(billingLogs, registryFieldValues, savedBillingOrder)
					}
					disabled={submitting}
					helperText={common.helperText}
				/>
			)
		}

		if (f.fieldType === 'dropdown') {
			const opts = sortFormFieldOptions(f)
			const displayForValue = (raw: string) => {
				if (raw === '') {
					return f.isRequired ? '' : 'None'
				}
				const match = opts.find((o) => o.value === raw)
				return match?.value ?? raw
			}

			if (f.fieldKey === 'project') {
				return (
					<Box key={f.id}>
						<OptionListAutocomplete<ProjectOption>
							key={f.id}
							id={`form-field-${f.id}`}
							options={projectOptions}
							value={value}
							onChange={(v) => setField(f.fieldKey, v)}
							getOptionLabel={(o) => `${o.projectNumber} - ${o.projectName}`}
							required={f.isRequired}
							disabled={common.disabled}
							label={common.label}
							helperText={common.helperText}
							placeholder={common.placeholder}
							size={common.size}
							fullWidth={common.fullWidth}
							noOptionsText='No matching projects'
						/>
						{renderWorkType()}
					</Box>
				)
			}

			return (
				<TextField
					key={f.id}
					select
					{...common}
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					/* Empty string is a valid controlled value; without shrink the label sits on top of the value ("None" overlapping "Material"). */
					InputLabelProps={{ shrink: true }}
					SelectProps={{
						displayEmpty: !f.isRequired,
						renderValue: (selected) => displayForValue(selected as string),
					}}
				>
					{!f.isRequired && <MenuItem value=''>None</MenuItem>}
					{opts.length === 0 ? (
						<MenuItem value='' disabled>
							No options configured
						</MenuItem>
					) : (
						opts.map((o) => (
							<MenuItem key={o.id} value={o.value}>
								{o.value}
							</MenuItem>
						))
					)}
				</TextField>
			)
		}

		if (f.fieldType === 'number' || f.fieldType === 'currency') {
			return (
				<TextField
					key={f.id}
					{...common}
					type='number'
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					inputProps={f.fieldType === 'currency' ? { step: '0.01' } : { step: 'any' }}
				/>
			)
		}

		if (f.fieldType === 'date') {
			return (
				<TextField
					key={f.id}
					{...common}
					type='date'
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					InputLabelProps={{ shrink: true }}
				/>
			)
		}

		if (f.fieldType === 'time') {
			return (
				<TextField
					key={f.id}
					{...common}
					type='time'
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					InputLabelProps={{ shrink: true }}
				/>
			)
		}

		if (f.fieldKey === 'wsrIdTag') {
			return (
				<TextField
					key={f.id}
					{...common}
					value={value}
					onChange={(e) => setField(f.fieldKey, e.target.value)}
					disabled
					sx={{
						'& .MuiInputBase-root.Mui-disabled': {
							bgcolor: 'action.disabledBackground',
						},
						'& .MuiInputBase-input.Mui-disabled': {
							color: 'text.primary',
							WebkitTextFillColor: 'inherit',
							opacity: 1,
						},
					}}
				/>
			)
		}

		const multiline =
			(f.helpText && f.helpText.length > 80) || f.fieldKey.toLowerCase().includes('notes')

		return (
			<TextField
				key={f.id}
				{...common}
				value={value}
				onChange={(e) => setField(f.fieldKey, e.target.value)}
				multiline={multiline}
				minRows={multiline ? 2 : undefined}
			/>
		)
	}

	let workTypeHelperText: string | undefined
	if (selectedWorkType) {
		workTypeHelperText = `Tag range: ${selectedWorkType.tagStart}–${selectedWorkType.tagEnd}`
	} else if (!selectedProject) {
		workTypeHelperText = 'Select a project first.'
	} else if (workTypes.length === 0) {
		workTypeHelperText = 'Configure work types in Project Forms first.'
	}

	const renderWorkType = () => (
		<TextField
			select
			label='Work type'
			size='small'
			fullWidth
			required={loadedTagId === null}
			value={selectedWorkType ? String(selectedWorkType.id) : ''}
			disabled={loadedTagId !== null || submitting || generating || workTypes.length === 0}
			InputLabelProps={{ shrink: true }}
			SelectProps={{ displayEmpty: true }}
			sx={{ mt: 1.25 }}
			helperText={workTypeHelperText}
			onChange={(e) => onRegistryPatch({ projectOptionWorkTypeId: e.target.value, wsrIdTag: '' })}
		>
			<MenuItem value='' disabled>
				Select work type
			</MenuItem>
			{workTypes.map((w) => (
				<MenuItem key={w.id} value={String(w.id)}>
					{w.name}
				</MenuItem>
			))}
		</TextField>
	)

	const renderProjectSection = (fields: FormField[]) => {
		const rows = chunkArray(fields, 2)
		return (
			<Grid container spacing={1.25}>
				{rows.map((pair, rowIdx) => (
					<React.Fragment key={rowIdx}>
						{pair.map((f) => (
							<Grid item xs={12} md={pair.length === 1 ? 12 : 6} key={f.id}>
								{renderField(f)}
							</Grid>
						))}
					</React.Fragment>
				))}
			</Grid>
		)
	}

	const renderWsrSection = (fields: FormField[]) => (
		<Stack spacing={1.25}>
			{fields.map((f) =>
				f.fieldKey === 'wsrIdTag' ? (
					<Box
						key={f.id}
						sx={{
							display: 'flex',
							flexDirection: { xs: 'column', sm: 'row' },
							gap: 1.25,
							alignItems: { xs: 'stretch', sm: 'flex-end' },
						}}
					>
						<Box sx={{ flex: 1, minWidth: 0 }}>{renderField(f)}</Box>
						<Button
							variant='contained'
							color='error'
							size='medium'
							disabled={submitting || generating || !selectedWorkType}
							onClick={async () => {
								if (!selectedWorkType) return
								setGenerating(true)
								try {
									const tagNumber = await getNextWsrTagNumber(selectedWorkType.id)
									if (loadedTagId !== null) onClear()
									onRegistryPatch({
										project: projectOption,
										projectOptionWorkTypeId: String(selectedWorkType.id),
										wsrIdTag: String(tagNumber),
									})
								} catch (e) {
									showErrorToast({
										message:
											(e as { message?: string })?.message || 'Failed to get next tag number.',
										traceId: '',
										eventId: '',
									})
								} finally {
									setGenerating(false)
								}
							}}
							sx={{ flexShrink: 0 }}
						>
							{generating ? 'GETTING TAG...' : 'SETUP NEW TAG'}
						</Button>
					</Box>
				) : (
					<Box key={f.id}>{renderField(f)}</Box>
				),
			)}
		</Stack>
	)

	const renderScaffoldSection = (fields: FormField[]) => (
		<Grid container spacing={1.25}>
			{fields.map((f) => (
				<Grid item xs={12} md={6} key={f.id}>
					{renderField(f)}
				</Grid>
			))}
		</Grid>
	)

	const renderDimensionsSection = (fields: FormField[]) => {
		const rows = buildFormFieldDimensionPairRows(fields)
		if (rows.length > 0) {
			return (
				<Box>
					<Grid container spacing={1.25}>
						{rows.map(([left, right], i) => (
							<React.Fragment key={i}>
								<Grid item xs={12} sm={6}>
									{renderField(left)}
								</Grid>
								<Grid item xs={12} sm={6}>
									{renderField(right)}
								</Grid>
							</React.Fragment>
						))}
					</Grid>
				</Box>
			)
		}
		return (
			<Grid container spacing={1.25}>
				{fields.map((f) => (
					<Grid item xs={12} sm={6} md={4} key={f.id}>
						{renderField(f)}
					</Grid>
				))}
			</Grid>
		)
	}

	const renderLocationSection = (fields: FormField[]) => {
		const rows = chunkArray(fields, 2)
		return (
			<Grid container spacing={1.25}>
				{rows.map((pair, rowIdx) => {
					const fullWidthRow =
						pair.some((f) => f.fieldKey.toLowerCase().includes('notes')) || pair.length === 1
					return (
						<React.Fragment key={rowIdx}>
							{pair.map((f) => (
								<Grid item xs={12} md={fullWidthRow ? 12 : 6} key={f.id}>
									{renderField(f)}
								</Grid>
							))}
						</React.Fragment>
					)
				})}
			</Grid>
		)
	}

	const renderSectionBody = (sectionName: string, fields: FormField[]) => {
		switch (sectionName) {
			case 'Project Information':
				return renderProjectSection(fields)
			case 'WSR Details':
				return renderWsrSection(fields)
			case 'Scaffold Spec/Type/Configuration':
				return renderScaffoldSection(fields)
			case 'Dimensions (Metric)':
				return renderDimensionsSection(fields)
			case 'Location Information':
				return renderLocationSection(fields)
			default:
				return renderProjectSection(fields)
		}
	}

	const renderSectionBlock = (sectionName: string, fields: FormField[]) => (
		<Box key={sectionName} sx={{ mb: 2.5 }}>
			<Typography variant='subtitle2' color='primary.main' sx={{ mb: 1, fontWeight: 600 }}>
				{sectionName}
			</Typography>
			<Divider sx={{ mb: 2 }} />
			{renderSectionBody(sectionName, fields)}
		</Box>
	)

	const cardTitle = formDetail?.name?.trim() || 'Work Scope Registry Info'
	const visibleCount = orderedSections.reduce((n, s) => n + s.fields.length, 0)

	return (
		<SectionCard title={cardTitle}>
			{loading && (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
					<CircularProgress size={28} />
				</Box>
			)}
			{!loading && loadError && (
				<Alert severity='warning' sx={{ mb: 2 }}>
					{loadError}
				</Alert>
			)}
			{!loading && !loadError && formDetail && visibleCount === 0 && (
				<Typography color='text.secondary'>This form has no visible fields.</Typography>
			)}
			{!loading && formDetail && visibleCount > 0 && (
				<>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							{leftSections.map((s) => renderSectionBlock(s.sectionName, s.fields))}
						</Grid>
						<Grid item xs={12} md={6}>
							{rightSections.map((s) => renderSectionBlock(s.sectionName, s.fields))}
						</Grid>
					</Grid>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							alignItems: 'center',
							gap: 1.5,
							flexWrap: 'wrap',
							mt: 2,
						}}
					>
						<Button
							variant='outlined'
							color='error'
							size='medium'
							onClick={onClear}
							disabled={submitting || generating}
							sx={{ color: 'error.main' }}
						>
							CLEAR
						</Button>
						<Button
							variant='outlined'
							color='primary'
							size='medium'
							sx={{ color: 'primary.main' }}
							disabled={updateDisabled || submitting || loading || !formDetail}
							onClick={onUpdateWorkscope}
						>
							UPDATE WORKSCOPE
						</Button>
						<Button
							variant='contained'
							color='success'
							size='medium'
							onClick={onSubmitNewWorkscope}
							disabled={
								submitDisabled ||
								submitting ||
								generating ||
								loading ||
								!formDetail ||
								!selectedWorkType
							}
						>
							{submitting ? 'SUBMITTING...' : 'SUBMIT NEW WORKSCOPE'}
						</Button>
					</Box>
				</>
			)}
		</SectionCard>
	)
}
